import { API_BASE_URL } from "@/config";
import { apiErrorFromStatus, networkError, ApiError } from "./errors";

/**
 * Access-token store + refresh coordinator.
 *
 * Security model (PRD FE-AUTH-04):
 *  - The access token lives ONLY in memory (never localStorage / URL / logs).
 *  - The session is restored via the HttpOnly `memos_refresh` cookie by calling
 *    POST /api/v1/auth/refresh.
 *
 * Concurrency model (PRD 8.2, §14.6):
 *  - Refresh tokens rotate: a concurrent refresh invalidates the old token.
 *  - Within this page, refreshes are de-duplicated (single-flight).
 *  - Across tabs, refresh results are shared over a BroadcastChannel so only one
 *    tab performs the actual refresh and the others adopt its token.
 */

interface TokenState {
  accessToken: string | null;
  /** Epoch ms when the access token expires. */
  expiresAt: number;
}

const state: TokenState = {
  accessToken: null,
  expiresAt: 0,
};

/** How long before expiry we treat the token as needing a refresh. */
const EXPIRY_SKEW_MS = 60_000;

export function getAccessToken(): string | null {
  return state.accessToken;
}

export function hasValidAccessToken(): boolean {
  return (
    state.accessToken !== null && Date.now() < state.expiresAt - EXPIRY_SKEW_MS
  );
}

export function setAccessToken(
  token: string | null,
  expiresAtIso?: string,
): void {
  state.accessToken = token;
  state.expiresAt = token && expiresAtIso ? Date.parse(expiresAtIso) || 0 : 0;
}

export function clearAccessToken(): void {
  state.accessToken = null;
  state.expiresAt = 0;
}

// ---------------------------------------------------------------------------
// Cross-tab coordination
// ---------------------------------------------------------------------------

type ChannelMessage =
  | { type: "refreshed"; accessToken: string; expiresAt: string }
  | { type: "signed-out" }
  | { type: "refresh-failed" };

const CHANNEL_NAME = "memos-auth";
const REFRESH_LOCK_NAME = "memos-auth-refresh";
const REFRESH_LOCK_KEY = "memos-auth-refresh-lock";
const LOCK_LEASE_MS = 15_000;
const LOCK_HEARTBEAT_MS = 5_000;
const LOCK_CONFIRM_MS = 25;
const LOCK_RETRY_MS = 50;
const TAB_ID =
  typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : Math.random().toString(36).slice(2);
let channel: BroadcastChannel | null = null;
const listeners = new Set<(msg: ChannelMessage) => void>();

function getChannel(): BroadcastChannel | null {
  if (typeof BroadcastChannel === "undefined") return null;
  if (!channel) {
    channel = new BroadcastChannel(CHANNEL_NAME);
    channel.onmessage = (ev: MessageEvent<ChannelMessage>) => {
      const msg = ev.data;
      if (msg.type === "refreshed") {
        // Adopt the token another tab refreshed for us.
        setAccessToken(msg.accessToken, msg.expiresAt);
      } else if (msg.type === "signed-out" || msg.type === "refresh-failed") {
        clearAccessToken();
      }
      if (msg.type === "refreshed") {
        crossTabResolver?.(msg.accessToken);
      } else {
        crossTabResolver?.(null);
      }
      listeners.forEach((l) => l(msg));
    };
  }
  return channel;
}

function broadcast(msg: ChannelMessage): void {
  getChannel()?.postMessage(msg);
}

/** Subscribe to cross-tab auth events. Returns an unsubscribe function. */
export function onAuthChannelMessage(
  listener: (msg: ChannelMessage) => void,
): () => void {
  getChannel();
  listeners.add(listener);
  return () => listeners.delete(listener);
}

// ---------------------------------------------------------------------------
// Single-flight refresh
// ---------------------------------------------------------------------------

let inFlightRefresh: Promise<string | null> | null = null;
/** Resolves the local refresh when another tab publishes the result. */
let crossTabResolver: ((token: string | null) => void) | null = null;

interface RefreshResponseBody {
  accessToken?: string;
  expiresAt?: string;
}

async function doRefreshRequest(): Promise<string | null> {
  let res: Response;
  try {
    res = await fetch(`${API_BASE_URL}/api/v1/auth/refresh`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: "{}",
    });
  } catch (e) {
    throw networkError(e);
  }

  if (!res.ok) {
    const body = await safeJson(res);
    clearAccessToken();
    broadcast({ type: "refresh-failed" });
    throw apiErrorFromStatus(body ?? {}, res.status);
  }

  const body = (await res.json()) as RefreshResponseBody;
  if (!body.accessToken) {
    clearAccessToken();
    broadcast({ type: "refresh-failed" });
    throw new ApiError({
      message: "Refresh response missing access token",
      httpStatus: res.status,
    });
  }
  setAccessToken(body.accessToken, body.expiresAt);
  broadcast({
    type: "refreshed",
    accessToken: body.accessToken,
    expiresAt: body.expiresAt ?? "",
  });
  return body.accessToken;
}

async function safeJson(
  res: Response,
): Promise<{ code?: number; message?: string; details?: unknown } | null> {
  try {
    return (await res.json()) as { code?: number; message?: string };
  } catch {
    return null;
  }
}

/**
 * Refresh the access token. Concurrent calls in this tab share one network
 * request; if another tab is refreshing we wait for its broadcast result
 * instead of racing the rotation.
 */
export function refreshAccessToken(): Promise<string | null> {
  if (inFlightRefresh) return inFlightRefresh;

  // Open the channel before entering the lock so a result published while this
  // tab is waiting can complete the call without another token rotation.
  getChannel();

  let resolvedByAnotherTab = false;
  let crossTabToken: string | null = null;

  const crossTab = new Promise<string | null>((resolve) => {
    crossTabResolver = (token) => {
      resolvedByAnotherTab = true;
      crossTabToken = token;
      resolve(token);
    };
  });

  const ownRefresh = withRefreshLock(
    async () => {
      if (resolvedByAnotherTab) return crossTabToken;
      if (hasValidAccessToken()) return getAccessToken();
      return doRefreshRequest();
    },
    () => resolvedByAnotherTab,
  );

  inFlightRefresh = Promise.race([ownRefresh, crossTab]).finally(() => {
    inFlightRefresh = null;
    crossTabResolver = null;
  });

  return inFlightRefresh;
}

async function withRefreshLock(
  refresh: () => Promise<string | null>,
  cancelled: () => boolean,
): Promise<string | null> {
  if (typeof navigator !== "undefined" && navigator.locks) {
    return navigator.locks.request(REFRESH_LOCK_NAME, async () => {
      // A previous owner posts its result before releasing the lock. Give that
      // queued BroadcastChannel task a chance to update this tab first.
      await delay(0);
      if (cancelled()) return null;
      return refresh();
    });
  }
  return withStorageRefreshLock(refresh, cancelled);
}

interface RefreshLockLease {
  owner: string;
  expiresAt: number;
}

async function withStorageRefreshLock(
  refresh: () => Promise<string | null>,
  cancelled: () => boolean,
): Promise<string | null> {
  const storage = getLockStorage();
  if (!storage) return refresh();

  const owner = `${TAB_ID}:${Math.random().toString(36).slice(2)}`;
  while (!cancelled()) {
    const now = Date.now();
    const current = readLease(storage);
    if (!current || current.expiresAt <= now) {
      try {
        storage.setItem(
          REFRESH_LOCK_KEY,
          JSON.stringify({ owner, expiresAt: now + LOCK_LEASE_MS }),
        );
      } catch {
        return refresh();
      }

      // localStorage has no compare-and-set operation. Waiting briefly and
      // verifying ownership makes concurrent contenders converge on one owner.
      await delay(LOCK_CONFIRM_MS);
      if (readLease(storage)?.owner === owner) {
        const heartbeat = globalThis.setInterval(() => {
          if (readLease(storage)?.owner !== owner) return;
          try {
            storage.setItem(
              REFRESH_LOCK_KEY,
              JSON.stringify({ owner, expiresAt: Date.now() + LOCK_LEASE_MS }),
            );
          } catch {
            // The existing lease will expire if storage becomes unavailable.
          }
        }, LOCK_HEARTBEAT_MS);
        try {
          if (cancelled()) return null;
          return await refresh();
        } finally {
          globalThis.clearInterval(heartbeat);
          if (readLease(storage)?.owner === owner) {
            try {
              storage.removeItem(REFRESH_LOCK_KEY);
            } catch {
              // The lease expires naturally if storage became unavailable.
            }
          }
        }
      }
    }
    await delay(LOCK_RETRY_MS + Math.floor(Math.random() * LOCK_RETRY_MS));
  }
  return null;
}

function getLockStorage(): Storage | null {
  try {
    return typeof localStorage === "undefined" ? null : localStorage;
  } catch {
    return null;
  }
}

function readLease(storage: Storage): RefreshLockLease | null {
  try {
    const value = storage.getItem(REFRESH_LOCK_KEY);
    if (!value) return null;
    const lease = JSON.parse(value) as Partial<RefreshLockLease>;
    if (
      typeof lease.owner !== "string" ||
      typeof lease.expiresAt !== "number"
    ) {
      return null;
    }
    return { owner: lease.owner, expiresAt: lease.expiresAt };
  } catch {
    return null;
  }
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => globalThis.setTimeout(resolve, ms));
}

/** Called by the session layer when a full sign-out occurs. */
export function notifySignedOut(): void {
  clearAccessToken();
  broadcast({ type: "signed-out" });
}
